import { TrendingUpIcon, Loader } from "lucide-react";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { useReports } from "../../hooks/useReports";
import formatCurrency from "../../utils/formatCurrency";

const Reports = () => {
  const {
    reportStats,
    categoryDistribution,
    customerGrowth,
    salesByCategory,
    salesTrend,
    loading,
    topPerformingProducts,
  } = useReports();

  const stats = [
    {
      title: "Total Revenue",
      value:
        reportStats.total_revenue != null
          ? formatCurrency(reportStats.total_revenue)
          : formatCurrency(0),
    },
    {
      title: "New Customers",
      value:
        reportStats.new_customers != null ? reportStats.new_customers : "0",
    },
    {
      title: "Order Count",
      value: reportStats.order_count != null ? reportStats.order_count : "0",
    },
    {
      title: "Average Order Value",
      value:
        reportStats.avg_order_value != null
          ? formatCurrency(reportStats.avg_order_value)
          : formatCurrency(0),
    },
  ];

  // Sales Trend Chart Data
  const salesData =
    salesTrend?.months && salesTrend?.counts
      ? salesTrend.months.map((month, i) => ({
          name: month,
          value: salesTrend.counts[i],
        }))
      : [];

  // Customer Growth Chart Data
  const customerData =
    customerGrowth?.months && customerGrowth?.counts
      ? customerGrowth.months.map((month, i) => ({
          month,
          users: customerGrowth.counts[i],
        }))
      : [];

  // Sales by Category Data
  const salesByCategoryData = Array.isArray(salesByCategory)
    ? salesByCategory.map((cat) => ({ name: cat.name, value: cat.count }))
    : [];

  // Category Distribution Data (for PieChart)
  const pieColors = [
    "#6B7143",
    "#F4A7B9",
    "#D9CBBF",
    "#00A3FF",
    "#BFAC93",
    "#A3B18A",
    "#F9C74F",
  ];
  const categoryData = Array.isArray(categoryDistribution)
    ? categoryDistribution.map((cat, i) => ({
        name: cat.name,
        value: cat.percentage,
        color: pieColors[i % pieColors.length],
      }))
    : [];

  // Top Performing Products
  const topProducts = Array.isArray(topPerformingProducts)
    ? topPerformingProducts
    : [];

  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 md:p-8 font-sans text-slate-700">
      {/* --- HEADER --- */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-8">
        <div>
          <h1 className="text-xl font-medium text-[#0C0C0E]">
            Reports & Analytics
          </h1>
          <p className="font-medium text-sm text-[#7D828D]">
            Comprehensive insights into your business performance
          </p>
        </div>
        {/*
        <button className="bg-white border border-slate-200 px-4 py-2 rounded-lg flex items-center gap-2 font-bold text-sm shadow-sm active:scale-95 transition-transform duration-100">
          Last 30 days <ChevronDown size={16} />
        </button>*/}
      </div>

      {/* --- KPI CARDS GRID --- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10 border-y border-[#EEF1F3]">
        {stats.map((stat, index) => (
          <div
            key={index}
            className={`p-4 sm:p-6 ${(index + 1) % 4 !== 0 ? "border-r border-[#EEF1F3]" : ""} ${index < stats.length - (stats.length % 4 || 4) ? "border-b border-[#EEF1F3]" : ""}`}
          >
            <p className="text-[#7D828D] font-medium text-sm mb-2">
              {stat.title}
            </p>
            <p className="text-2xl font-medium text-slate-900 mb-2">
              {stat.value}
            </p>
            {stat.change && (
              <div
                className={`flex items-center gap-1 text-[11px] font-medium ${stat.isUp ? "text-green-600" : "text-rose-500"}`}
              >
                {stat.isUp ? "↑" : "↓"} {stat.change}
                <span className="text-slate-400 ml-1">vs last month</span>
              </div>
            )}
            {stat.subtitle && (
              <p className="text-[11px] text-slate-400 font-medium">
                {stat.subtitle}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* --- CHARTS SECTION --- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Sales Trend */}
        <ChartContainer title="Sales Trend">
          {loading ? (
            <div className="flex items-center justify-center h-[180px]">
              <Loader size={32} className="text-[#6B7143] animate-spin" />
            </div>
          ) : salesData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={salesData}>
                <defs>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6B7143" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#6B7143" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#f0f0f0"
                />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#94a3b8" }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#94a3b8" }}
                />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#6B7143"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorValue)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-[180px] text-slate-400 text-sm">
              <span className="mb-2">No sales data available</span>
            </div>
          )}
        </ChartContainer>

        {/* Customer Growth */}
        <ChartContainer title="Customer Growth">
          {loading ? (
            <div className="flex items-center justify-center h-[180px]">
              <Loader size={32} className="text-[#6B7143] animate-spin" />
            </div>
          ) : customerData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={customerData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#f0f0f0"
                />
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#94a3b8" }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#94a3b8" }}
                />
                <Tooltip cursor={{ fill: "#f8fafc" }} />
                <Bar
                  dataKey="users"
                  fill="#F4A7B9"
                  radius={[4, 4, 0, 0]}
                  barSize={30}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-[180px] text-slate-400 text-sm">
              <span className="mb-2">No customer growth data available</span>
            </div>
          )}
        </ChartContainer>

        {/* Sales by Category */}
        <ChartContainer title="Sales by Category">
          {loading ? (
            <div className="flex items-center justify-center h-[180px]">
              <Loader size={32} className="text-[#6B7143] animate-spin" />
            </div>
          ) : salesByCategoryData.length > 0 ? (
            <div className="space-y-4 pt-4">
              {salesByCategoryData.map((cat, idx) => (
                <div key={cat.name} className="space-y-1">
                  <div className="flex justify-between text-xs font-semibold text-slate-500">
                    <span>{cat.name}</span>
                    <span>{cat.value}</span>
                  </div>
                  <div className="w-full bg-slate-100 h-6 rounded-md overflow-hidden">
                    <div
                      className="h-full rounded-md"
                      style={{
                        width: `${cat.value * 10}%`,
                        backgroundColor: pieColors[idx % pieColors.length],
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[180px] text-slate-400 text-sm">
              <span className="mb-2">No sales by category data available</span>
            </div>
          )}
        </ChartContainer>

        {/* Category Distribution */}
        <ChartContainer title="Product Category Distribution">
          {loading ? (
            <div className="flex items-center justify-center h-[180px]">
              <Loader size={32} className="text-[#6B7143] animate-spin" />
            </div>
          ) : categoryData.length > 0 ? (
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 16 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                {categoryData.map((cat) => (
                  <div
                    key={cat.name}
                    className="flex items-center gap-2 text-[10px] font-bold"
                  >
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: cat.color }}
                    ></div>
                    <span className="text-slate-400">
                      {cat.name} {cat.value}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[180px] text-slate-400 text-sm">
              <span className="mb-2">
                No category distribution data available
              </span>
            </div>
          )}
        </ChartContainer>
      </div>

      {/* --- TOP PERFORMING PRODUCTS TABLE --- */}
      <h2 className="text-xl font-semibold mb-4 text-[#0C0C0E]">
        Top Performing Products
      </h2>
      <div className="border border-[#EEF1F3] rounded-lg overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16 px-6">
            <Loader size={32} className="text-[#6B7143] animate-spin" />
          </div>
        ) : topProducts.length > 0 ? (
          <>
            <table className="min-w-full text-left border-collapse text-xs sm:text-sm">
              <thead>
                <tr className="bg-[#FBF8F6] border-b border-[#EEF1F3]">
                  <th className="px-3 sm:px-6 py-3 sm:py-4 w-16 text-xs sm:text-sm font-medium text-[#7D828D]">
                    Rank
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium text-[#7D828D]">
                    Product
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium text-[#7D828D]">
                    Category
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm font-medium text-[#7D828D]">
                    Units Sold
                  </th>
                  <th className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium text-[#7D828D]">
                    Revenue
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF1F3]">
                {topProducts.map((p, idx) => (
                  <tr key={idx} className="text-xs sm:text-sm">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 font-medium text-slate-900">
                      {p.rank || idx + 1}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <span className="font-medium text-slate-900">
                          {p.title}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-slate-500 font-medium">
                      {p.cat || p.category || "-"}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-center font-medium text-slate-900">
                      {p.units_sold || "-"}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 font-medium text-slate-900">
                      {p.revenue != null ? formatCurrency(p.revenue) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          /* Empty State */
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <div className="p-1 border border-slate-300 bg-[#F8F5F1] rounded-full overflow-hidden mb-4">
              <TrendingUpIcon size={32} className="text-[#BFAC93]" />
            </div>
            <h4 className="font-medium text-base text-[#0C0C0E] text-center mb-2">
              No reports yet
            </h4>
            <p className="text-sm text-slate-400 text-center">
              Reports will appear here as products are sold
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper Components
const ChartContainer = ({ title, children }) => (
  <div className="bg-white p-4 sm:p-6 rounded-2xl border border-slate-200 shadow-sm">
    <h2 className="text-base font-bold text-slate-900 mb-4 sm:mb-6">{title}</h2>
    {children}
  </div>
);

export default Reports;
